import React, { Suspense, lazy } from 'react';
import './App.css';
import { BrowserRouter, Routes, Route, useNavigate, useLocation, useParams } from 'react-router-dom';
import { AuthProvider } from './context/AuthContext';
import Home from './pages/Home';
import Menu from './pages/Menu';
import Login from './pages/Login';
import Register from './pages/Register';
import ForgotPassword from './pages/ForgotPassword';
import ResetPassword from './pages/ResetPassword';
import VerifyEmail from './pages/VerifyEmail';
import Profil from './pages/Profil';
import AProposPage, { NotreHistoirePage, RemerciementsPage } from './pages/AProposPage';
import PremiumRoute from './components/PremiumRoute';
import { MonthlyBilanGate } from './components/MonthlyBilan';
import { preloadMeditationIcons } from './preloadAssets';

// Pages de contenu chargées uniquement lorsqu'elles deviennent nécessaires
const AideContact = lazy(() => import('./pages/AideContact'));
const OffrirCadeau = lazy(() => import('./pages/OffrirCadeau'));
const GiftActivation = lazy(() => import('./pages/GiftActivation'));
const MesCadeaux = lazy(() => import('./pages/MesCadeaux'));
const MeditationCategories = lazy(() => import('./pages/MeditationCategories'));
const MeditationPage = lazy(() => import('./pages/MeditationPage'));

// Parcours strictement Premium : l'URL directe ne doit jamais ouvrir la liste
const PREMIUM_ONLY_MEDITATION_SLUGS = ['repos-profond', 'sommeil-repos'];
const GuardedMeditationPage = () => {
  const { categorySlug } = useParams();
  if (PREMIUM_ONLY_MEDITATION_SLUGS.includes(categorySlug)) {
    return <PremiumRoute redirectTo="/meditation"><MeditationPage /></PremiumRoute>;
  }
  return <MeditationPage />;
};
const DhikrCategories = lazy(() => import('./pages/DhikrCategories'));
const DhikrList = lazy(() => import('./pages/DhikrList'));
const NomsDAllah = lazy(() => import('./pages/NomsDAllah'));
const ProphetePage = lazy(() => import('./pages/ProphetePage'));
const DhikrPage = lazy(() => import('./pages/DhikrPage'));
const RespirationCategories = lazy(() => import('./pages/RespirationCategories'));
const RespirationPage = lazy(() => import('./pages/RespirationPage'));
const RespirationCarreePage = lazy(() => import('./pages/RespirationCarreePage'));
const RespirationApaisantePage = lazy(() => import('./pages/RespirationApaisantePage'));
const BrainBreakPage = lazy(() => import('./pages/BrainBreakPage'));
const RespirationAbdominalePage = lazy(() => import('./pages/RespirationAbdominalePage'));
const Respiration478Page = lazy(() => import('./pages/Respiration478Page'));
const CGV = lazy(() => import('./pages/CGV'));
const Ambiance = lazy(() => import('./pages/Ambiance'));
const JournalPage = lazy(() => import('./pages/JournalPage'));
const Abonnement = lazy(() => import('./pages/Abonnement'));
const FavorisPage = lazy(() => import('./pages/FavorisPage'));
const AdminPanel = lazy(() => import('./pages/AdminPanel'));
const SharedMeditation = lazy(() => import('./pages/SharedMeditation'));

// Fond ivoire pendant le chargement d'une page — pas d'écran blanc ni de saut visuel
const PageLoading = () => <div className="min-h-screen bg-[#FAF9F6]" aria-hidden="true" />;

// À chaque véritable ouverture (lancement, nouvel onglet, actualisation manuelle),
// on passe d'abord par l'écran logo. Flag en mémoire : il se réinitialise à chaque
// vrai chargement de page, mais persiste pendant la navigation interne SPA.
const SplashGate = () => {
  const navigate = useNavigate();
  const location = useLocation();
  React.useEffect(() => {
    if (window.__spiritulSplashSeen) return;
    const p = location.pathname;
    if (p !== '/' && !p.startsWith('/shared') && !p.startsWith('/admin')
        && !p.startsWith('/reset-password') && !p.startsWith('/verify-email') && !p.startsWith('/mot-de-passe-oublie')
        && !p.startsWith('/cadeau/')) {
      sessionStorage.setItem('splash_target', p + location.search);
      navigate('/', { replace: true });
    }
  }, []);
  return null;
};

function App() {
  React.useEffect(() => {
    const t = setTimeout(preloadMeditationIcons, 800);
    return () => clearTimeout(t);
  }, []);
  return (
    <div className="App">
      <AuthProvider>
        <BrowserRouter>
          <SplashGate />
          <MonthlyBilanGate />
          <Suspense fallback={<PageLoading />}>
          <Routes>
            <Route path="/" element={<Home />} />
            <Route path="/login" element={<Login />} />
            <Route path="/register" element={<Register />} />
            <Route path="/mot-de-passe-oublie" element={<ForgotPassword />} />
            <Route path="/reset-password" element={<ResetPassword />} />
            <Route path="/verify-email" element={<VerifyEmail />} />
            <Route path="/menu" element={<Menu />} />
            <Route path="/meditation" element={<MeditationCategories />} />
            <Route path="/meditation/:categorySlug" element={<GuardedMeditationPage />} />
            <Route path="/dhikr" element={<DhikrCategories />} />
            <Route path="/dhikr/prophete" element={<ProphetePage />} />
            <Route path="/dhikr/noms-divins" element={<NomsDAllah />} />
            <Route path="/dhikr/noms-divins/:subcategorySlug" element={<NomsDAllah />} />
            <Route path="/dhikr/:categorySlug" element={<DhikrList />} />
            <Route path="/dhikr/:categorySlug/:slug" element={<DhikrPage />} />
            <Route path="/dhikr/noms-divins/:subcategorySlug/:slug" element={<DhikrPage />} />
            <Route path="/dhikr-session/:slug" element={<DhikrPage />} />
            <Route path="/respiration" element={<RespirationCategories />} />
            <Route path="/respiration/respiration-apaisante" element={<RespirationApaisantePage />} />
            <Route path="/respiration/coherence-cardiaque" element={<RespirationPage />} />
            <Route path="/respiration/respiration-carree" element={<PremiumRoute redirectTo="/respiration"><RespirationCarreePage /></PremiumRoute>} />
            <Route path="/respiration/brain-break" element={<BrainBreakPage />} />
            <Route path="/respiration/respiration-abdominale" element={<PremiumRoute redirectTo="/respiration"><RespirationAbdominalePage /></PremiumRoute>} />
            <Route path="/respiration/respiration-478" element={<PremiumRoute redirectTo="/respiration"><Respiration478Page /></PremiumRoute>} />
            <Route path="/ambiance" element={<Ambiance />} />
            <Route path="/favoris" element={<FavorisPage />} />
            <Route path="/a-propos" element={<AProposPage />} />
            <Route path="/a-propos/histoire" element={<NotreHistoirePage />} />
            <Route path="/a-propos/remerciements" element={<RemerciementsPage />} />
            <Route path="/aide" element={<AideContact />} />
            <Route path="/offrir-cadeau" element={<OffrirCadeau />} />
            <Route path="/cadeau/:token" element={<GiftActivation />} />
            <Route path="/mes-cadeaux" element={<MesCadeaux />} />
            <Route path="/shared/meditation/:sessionId" element={<SharedMeditation />} />
            <Route path="/profil" element={<Profil />} />
            <Route path="/journal" element={<JournalPage />} />
            <Route path="/abonnement" element={<Abonnement />} />
            <Route path="/cgv" element={<CGV />} />
            <Route path="/admin" element={<AdminPanel />} />
          </Routes>
          </Suspense>
        </BrowserRouter>
      </AuthProvider>
    </div>
  );
}

export default App;
